import cx from 'classnames';
import {debounce, find, isEmpty, map} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {conversationsApi} from 'api/conversations';
import {
    PageHeader, Icon, IconName, Initials, Input, Loader, TextChatMessageList, UserParticipantIds, Button, Chip,
    Dropdown, DropdownItem, ParticipantDetailsModal, ConfirmModal, TextArea, ConfirmButton
} from 'components';
import {AuthLayoutRouteContext, UserContext} from 'context';
import {SocketEvent} from 'enums';
import {useObservable, useScrollToBottom, useSocketEventCallback} from 'hooks';
import {AnyParticipant} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {authDataStore} from 'services';
import {
    ConversationChannel, Conversation,
    ConversationStatus, ConversationMessage, ParticipantRole, TextChatContentType, TextChatMessage,
    ConversationChannelIntegrationData,
} from 'types';
import {dateUtils} from 'utils/date';
import {sessionUtils} from 'utils/session';
import {formatDate} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE;

type TabType = 'active' | 'archived';

const ROLE_MAP: Record<string, ParticipantRole> = {
    agent: ParticipantRole.Agent,
    client: ParticipantRole.Client,
};

function toAnyParticipant(id: string, role: ParticipantRole, data: Record<string, unknown>): AnyParticipant {
    return {
        id,
        role,
        data,
        asyncRequests: [],
        socket: {connected: false, date: new Date()},
    } as AnyParticipant;
}

function channelParticipantsToAnyParticipants(
    participants: ConversationChannel['participants'],
    client?: Conversation['client'],
): AnyParticipant[] {
    const result: AnyParticipant[] = (participants || []).map((p) =>
        toAnyParticipant(p.id, ROLE_MAP[p.role] || ParticipantRole.Agent, {eid: p.eid, firstName: p.name}),
    );
    if (client?.userInfo?.id) {
        result.push(
            toAnyParticipant(client.userInfo.id, ParticipantRole.Client, {
                userInfo: {firstName: client.userInfo.firstName, lastName: client.userInfo.lastName},
            }),
        );
    }
    return result;
}

function channelMessagesToTextChat(
    channel: ConversationChannel,
    participants: ConversationChannel['participants'],
): Partial<TextChatMessage>[] {
    return (channel.messages || []).map((m, index) => {
        const participant = participants?.find((p) => p.id === m.participantId);
        return {
            id: m.id,
            content: m.content,
            contentType: m.contentType as TextChatContentType,
            date: m.date,
            index,
            data: m.data as TextChatMessage['data'],
            from: {
                id: m.participantId,
                name: participant?.name || m.participantId,
            },
        };
    });
}

function getChannelInfo(type: string): {label: string; icon: IconName} {
    switch (type) {
        case 'whatsapp': return {label: 'WhatsApp', icon: 'whatsapp'};
        case 'telegram': return {label: 'Telegram', icon: 'telegram'};
        default: return {label: type, icon: 'chatbot'};
    }
}

function getChannelContactInfo(channel: ConversationChannel): string {
    const d: ConversationChannelIntegrationData = channel.integrationData;
    if (!d) {
        return '';
    }
    if (channel.type === 'whatsapp') {
        return d.phone || '';
    }
    if (channel.type === 'telegram') {
        return d.login || d.chatId || '';
    }
    return '';
}

export const Conversations: React.FC = () => {
    const {t} = useTranslation();
    const {setAuthLayoutProps} = useContext(AuthLayoutRouteContext);
    const {socket, settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    const {localUser} = useObservable(authDataStore);

    const [activeTab, setActiveTab] = useState<TabType>('active');
    const [selectedChannelIndex, setSelectedChannelIndex] = useState<number>(0);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [archivedConversations, setArchivedConversations] = useState<Conversation[]>([]);
    const [archivedPage, setArchivedPage] = useState<number>(1);
    const [archivedPages, setArchivedPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [archivedLoading, setArchivedLoading] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<string>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [chatMessages, setChatMessages] = useState<Partial<TextChatMessage>[]>([]);
    const [chatChannel, setChatChannel] = useState<ConversationChannel>(null);
    const [chatLoading, setChatLoading] = useState<boolean>(false);
    const [endingConversation, setEndingConversation] = useState<boolean>(false);

    // Cache of loaded channels for the current conversation
    const [loadedChannels, setLoadedChannels] = useState<ConversationChannel[]>([]);

    // Join/Leave + message input state
    const [joined, setJoined] = useState<boolean>(false);
    const [messageText, setMessageText] = useState<string>('');
    const [sending, setSending] = useState<boolean>(false);
    const [joining, setJoining] = useState<boolean>(false);
    const [leaving, setLeaving] = useState<boolean>(false);
    const [rejoining, setRejoining] = useState<boolean>(false);
    const [transferModalOpen, setTransferModalOpen] = useState<boolean>(false);
    const [transferMessage, setTransferMessage] = useState<string>('');

    // Mobile responsive state
    const [mobilePanelView, setMobilePanelView] = useState<'list' | 'chat'>('list');

    // Track last seen message count per channel for unread indicators
    const [lastSeenMessageCounts, setLastSeenMessageCounts] = useState<Record<string, number>>({});

    // Track last seen timestamps for new message indicator on conversations
    const [lastSeenTimestamps, setLastSeenTimestamps] = useState<Record<string, string>>({});

    // Participant details modal state
    const [participantModalProps, setParticipantModalProps] = useState<{
        open: boolean;
        participant?: AnyParticipant;
    }>({open: false});

    const conversationsRef = useRef<Conversation[]>([]);

    const {messagesEndRef} = useScrollToBottom({
        triggerDep: chatMessages.length,
        disabled: chatLoading,
        bottomSpacing: 12,
    });

    const applyConversationChanges = useCallback(() => {
        setConversations([...conversationsRef.current]);
        setTotalCount(conversationsRef.current.length);
    }, []);

    const setConversationsDebounced = useMemo(
        () => debounce(applyConversationChanges, 500, {maxWait: 1000}),
        [applyConversationChanges],
    );

    const isArchivedTab = activeTab === 'archived';
    const displayedItems = isArchivedTab ? archivedConversations : conversations;

    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) {
            return displayedItems;
        }
        const query = searchQuery.trim().toLowerCase();
        return displayedItems.filter((c) => {
            const userInfo = c.client?.userInfo;
            const primary = userInfo?.phone || userInfo?.email || userInfo?.id || '';
            const name = [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ');
            return primary.toLowerCase().includes(query) || name.toLowerCase().includes(query);
        });
    }, [displayedItems, searchQuery]);

    const selectedConversation = useMemo(() =>
        find(displayedItems, {id: selectedId}), [displayedItems, selectedId]);

    // Get the selected channel ref from the conversation's lightweight channels array
    const selectedChannelRef = useMemo(() =>
        selectedConversation?.channels?.[selectedChannelIndex] ?? selectedConversation?.channels?.[0],
    [selectedConversation, selectedChannelIndex]);

    const isAgentJoinedChannel = useMemo(() => {
        if (!localUser?.eid) {
            return false;
        }
        // Prefer chatChannel (full loaded channel with `active` field)
        if (!chatChannel) {
            return false;
        }
        return chatChannel.participants?.some((p) => p.id === String(localUser.eid) && p.active !== false) || false;
    }, [chatChannel, localUser]);

    const fetchConversations = useCallback(async () => {
        try {
            const data = await conversationsApi.getConversations();
            conversationsRef.current = data || [];
            setConversations(data || []);
            setTotalCount((data || []).length);
            // Mark all as seen on initial load
            setLastSeenTimestamps((prev) => {
                const next = {...prev};
                (data || []).forEach((c) => {
                    if (!next[c.id]) {
                        next[c.id] = c.lastActivityAt;
                    }
                });
                return next;
            });
            setIsError(false);
            setIsLoading(false);
        } catch {
            setIsError(true);
            setIsLoading(false);
        }
    }, []);

    const fetchArchivedConversations = useCallback(async (page = 1, append = false) => {
        setArchivedLoading(true);
        try {
            const data = await conversationsApi.getArchived(page);
            setArchivedConversations((prev) => append ? [...prev, ...data.items] : data.items);
            setArchivedPage(data.page);
            setArchivedPages(data.pages);
            setTotalCount(data.count);
        } catch {
            if (!append) {
                setArchivedConversations([]);
            }
        } finally {
            setArchivedLoading(false);
        }
    }, []);

    const loadChannel = useCallback(async (conversationId: string, channelId: string) => {
        try {
            const channel = await conversationsApi.getChannel(conversationId, channelId);
            if (channel) {
                setChatChannel(channel);
                setChatMessages(channelMessagesToTextChat(channel, channel.participants));
                setLoadedChannels((prev) => {
                    const filtered = prev.filter((ch) => ch.id !== channel.id);
                    return [...filtered, channel];
                });
            } else {
                setChatChannel(null);
                setChatMessages([]);
            }
        } catch {
            setChatChannel(null);
            setChatMessages([]);
        }
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedId(null);
        setChatMessages([]);
        setChatChannel(null);
        setLoadedChannels([]);
        setJoined(false);
        setMessageText('');
        setMobilePanelView('list');
        setSelectedChannelIndex(0);
        setLastSeenMessageCounts({});
    }, []);

    const handleConversationClick = useCallback((item: Conversation) => {
        if (selectedId === item.id) {
            clearSelection();
            return;
        }
        setSelectedId(item.id);
        setSelectedChannelIndex(0);
        setMobilePanelView('chat');
        setJoined(false);
        setMessageText('');
        setLoadedChannels([]);

        // Mark conversation as seen
        if (item.lastActivityAt) {
            setLastSeenTimestamps((prev) => ({...prev, [item.id]: item.lastActivityAt}));
        }

        // Load first channel
        const firstChannelRef = item.channels?.[0];
        if (firstChannelRef) {
            setChatLoading(true);
            setChatMessages([]);
            loadChannel(item.id, firstChannelRef.channelId).finally(() => setChatLoading(false));
        } else {
            setChatChannel(null);
            setChatMessages([]);
        }

    }, [selectedId, clearSelection, loadChannel]);

    const handleChannelClick = useCallback((index: number) => {
        if (!selectedConversation) {
            return;
        }
        setSelectedChannelIndex(index);

        const channelRef = selectedConversation.channels?.[index];
        if (!channelRef) {
            return;
        }

        // Check if already loaded
        const cached = loadedChannels.find((ch) => ch.id === channelRef.channelId);
        if (cached) {
            setChatChannel(cached);
            setChatMessages(channelMessagesToTextChat(cached, cached.participants));
            return;
        }

        // Load channel
        setChatLoading(true);
        loadChannel(selectedConversation.id, channelRef.channelId).finally(() => setChatLoading(false));
    }, [selectedConversation, loadedChannels, loadChannel]);

    const handleEndConversation = useCallback(async () => {
        if (!selectedId || endingConversation) {
            return;
        }
        setEndingConversation(true);
        try {
            await conversationsApi.archiveConversation(selectedId);
            clearSelection();
            await fetchConversations();
        } catch {
            // ignore
        } finally {
            setEndingConversation(false);
        }
    }, [selectedId, endingConversation, clearSelection, fetchConversations]);

    const handleJoin = useCallback(async () => {
        if (!selectedId || !selectedChannelRef || joining) {
            return;
        }
        setJoining(true);
        try {
            await conversationsApi.joinChannel(selectedId, selectedChannelRef.channelId);
            setJoined(true);
            // Reload the channel to get updated participants
            await loadChannel(selectedId, selectedChannelRef.channelId);
            await fetchConversations();
        } catch {
            // ignore
        } finally {
            setJoining(false);
        }
    }, [selectedId, selectedChannelRef, joining, fetchConversations, loadChannel]);

    const handleLeave = useCallback(async (message?: string) => {
        if (!selectedId || !selectedChannelRef || leaving) {
            return;
        }
        setTransferModalOpen(false);
        setLeaving(true);
        try {
            await conversationsApi.leaveChannel(
                selectedId,
                selectedChannelRef.channelId,
                message ? {message} : undefined,
            );
            setJoined(false);
            setMessageText('');
            // Reload the channel to get updated participants
            await loadChannel(selectedId, selectedChannelRef.channelId);
            await fetchConversations();
        } catch {
            // ignore
        } finally {
            setLeaving(false);
        }
    }, [selectedId, selectedChannelRef, leaving, fetchConversations, loadChannel]);

    const handleTransferToAiClick = useCallback(() => {
        setTransferMessage('');
        setTransferModalOpen(true);
    }, []);

    const handleSendMessage = useCallback(async () => {
        if (!selectedId || !selectedChannelRef || !messageText.trim() || sending) {
            return;
        }
        setSending(true);
        try {
            await conversationsApi.sendMessage(selectedId, selectedChannelRef.channelId, messageText.trim());
            setMessageText('');
            await loadChannel(selectedId, selectedChannelRef.channelId);
        } catch {
            // ignore
        } finally {
            setSending(false);
        }
    }, [selectedId, selectedChannelRef, messageText, sending, loadChannel]);

    const handleRejoin = useCallback(async () => {
        if (!selectedId || rejoining) {
            return;
        }
        setRejoining(true);
        try {
            const result = await conversationsApi.rejoinConversation(selectedId);
            setActiveTab('active');
            await fetchConversations();
            await fetchArchivedConversations();
            if (result?.conversationId) {
                setSelectedId(result.conversationId);
                setChatMessages([]);
                setChatChannel(null);
                setLoadedChannels([]);
                setJoined(false);
                setMessageText('');
                setMobilePanelView('chat');
                setSelectedChannelIndex(0);

                // Load first channel of restored conversation
                const restored = conversationsRef.current.find((c) => c.id === result.conversationId);
                const firstChannel = restored?.channels?.[0];
                if (firstChannel) {
                    setChatLoading(true);
                    loadChannel(result.conversationId, firstChannel.channelId)
                        .finally(() => setChatLoading(false));
                }
            } else {
                clearSelection();
            }
        } catch {
            // ignore
        } finally {
            setRejoining(false);
        }
    }, [selectedId, rejoining, clearSelection, fetchConversations, fetchArchivedConversations, loadChannel]);

    const handleLoadMore = useCallback(() => {
        if (archivedPage < archivedPages) {
            fetchArchivedConversations(archivedPage + 1, true);
        }
    }, [archivedPage, archivedPages, fetchArchivedConversations]);

    const handleTabChange = useCallback((tab: TabType) => {
        setActiveTab(tab);
        clearSelection();
        setTotalCount(0);
        if (tab === 'archived') {
            setArchivedPage(1);
            fetchArchivedConversations(1);
        } else {
            setTotalCount(conversations.length);
        }
    }, [clearSelection, fetchArchivedConversations, conversations.length]);

    const getClientInfo = useCallback((item: Conversation) => {
        const userInfo = item.client?.userInfo;
        const name = [userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ');
        const id = userInfo?.id;
        const primary = name || id || item.id?.substring(0, 8);
        const secondary = userInfo?.phone || userInfo?.email || (name && id ? id : '');
        return {primary, secondary};
    }, []);

    // Initial load
    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    // Socket: conversation changed — update or add to list
    useSocketEventCallback(socket, SocketEvent.ConversationChanged,
        useCallback((conversation: Conversation) => {
            if (!conversation) {
                return;
            }
            const prev = conversationsRef.current;
            const idx = prev.findIndex((c) => c.id === conversation.id);
            let updated;
            if (idx >= 0) {
                updated = prev.map((c) => c.id === conversation.id ? conversation : c);
            } else {
                updated = [...prev, conversation];
            }
            conversationsRef.current = updated.sort(
                (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime(),
            );
            setConversationsDebounced();
        }, [setConversationsDebounced])
    );

    // Socket: conversation deleted — remove from list
    useSocketEventCallback(socket, SocketEvent.ConversationDeleted,
        useCallback(({id}: {id: string}) => {
            if (!id) {
                return;
            }
            conversationsRef.current = conversationsRef.current.filter((c) => c.id !== id);
            setConversationsDebounced();
        }, [setConversationsDebounced])
    );

    // Join/leave conversation room when selecting/deselecting
    useEffect(() => {
        if (selectedId && !isArchivedTab && socket) {
            socket.emit(SocketEvent.ConversationSubscribe, {conversationId: selectedId});
        }
        return () => {
            if (selectedId && !isArchivedTab && socket) {
                socket.emit(SocketEvent.ConversationUnsubscribe, {conversationId: selectedId});
            }
        };
    }, [selectedId, isArchivedTab, socket]);

    // Socket: new message in selected conversation
    useSocketEventCallback(socket, SocketEvent.ConversationNewMessage,
        useCallback((payload: {conversationId: string; channelId: string; message: ConversationMessage}) => {
            if (!payload || payload.conversationId !== selectedId) {
                return;
            }

            if (!chatChannel || payload.channelId !== chatChannel.id) {
                return;
            }

            // Deduplicate — check if message already exists
            setChatMessages((prev) => {
                if (prev.some((m) => m.id === payload.message.id)) {
                    return prev;
                }
                const participant = chatChannel.participants?.find((p) => p.id === payload.message.participantId);
                const newMsg: Partial<TextChatMessage> = {
                    id: payload.message.id,
                    content: payload.message.content,
                    contentType: payload.message.contentType as TextChatContentType,
                    date: payload.message.date,
                    data: payload.message.data as TextChatMessage['data'],
                    index: prev.length,
                    from: {
                        id: payload.message.participantId,
                        name: participant?.name || payload.message.participantId,
                    },
                };
                return [...prev, newMsg];
            });
        }, [selectedId, chatChannel])
    );

    // Socket: reconnection — reload conversation list
    useSocketEventCallback(socket, SocketEvent.Authenticated,
        useCallback(() => {
            fetchConversations();
        }, [fetchConversations])
    );

    // Cleanup debounce on unmount
    useEffect(() => () => {
        setConversationsDebounced.cancel();
    }, [setConversationsDebounced]);

    // Clear selected if it disappears from the list
    useEffect(() => {
        if (selectedId && !find(displayedItems, {id: selectedId})) {
            clearSelection();
        }
    }, [displayedItems, selectedId, clearSelection]);

    useEffect(() => {
        setAuthLayoutProps({});
    }, [setAuthLayoutProps]);

    // Build participants array for TextChatMessageList compatibility
    // Use chatChannel (from full DTO) which includes all participants (including inactive)
    const chatParticipants = useMemo((): AnyParticipant[] => {
        if (!chatChannel) {
            return [];
        }
        return channelParticipantsToAnyParticipants(chatChannel.participants, selectedConversation?.client);
    }, [chatChannel, selectedConversation]);

    const userParticipantIds: UserParticipantIds = useMemo(() => {
        const agentIds = (chatChannel?.participants || [])
            .filter((p) => p.role === 'agent')
            .map((p) => p.id);
        return {agent: agentIds, assistant: []};
    }, [chatChannel]);

    const localParticipantId = useMemo(() => {
        if (!localUser?.eid) {
            return null;
        }
        const agentP = chatChannel?.participants?.find((p) => p.id === String(localUser.eid));
        return agentP?.id || null;
    }, [chatChannel, localUser]);

    const handleMobileBack = useCallback(() => {
        clearSelection();
        setMobilePanelView('list');
    }, [clearSelection]);

    // Update last seen message count when viewing a channel
    useEffect(() => {
        if (!chatChannel?.id) {
            return;
        }
        const count = chatChannel.messageCount ?? chatChannel.messages?.length ?? 0;
        if (count > 0) {
            setLastSeenMessageCounts((prev) => {
                if (prev[chatChannel.id] === count) {
                    return prev;
                }
                return {...prev, [chatChannel.id]: count};
            });
        }
    }, [chatChannel]);

    const hasUnreadForChannel = useCallback((channelId: string): boolean => {
        if (!selectedConversation || channelId === chatChannel?.id) {
            return false;
        }
        // With lazy loading, we don't have messageCount in the lightweight ref.
        // We can only compare against lastSeenMessageCounts if we've loaded this channel before.
        const lastSeen = lastSeenMessageCounts[channelId];
        if (lastSeen === undefined) {
            return false;
        }
        // Check if cached channel has more messages
        const cached = loadedChannels.find((ch) => ch.id === channelId);
        if (!cached) {
            return false;
        }
        const currentCount = cached.messageCount ?? cached.messages?.length ?? 0;
        return currentCount > lastSeen;
    }, [selectedConversation, chatChannel, lastSeenMessageCounts, loadedChannels]);

    const hasAnyUnread = useMemo(() => {
        if (!selectedConversation?.channels) {
            return false;
        }
        return selectedConversation.channels.some(
            (ch) => ch.channelId !== chatChannel?.id && hasUnreadForChannel(ch.channelId),
        );
    }, [selectedConversation, chatChannel, hasUnreadForChannel]);

    return (
        <div className='tw-flex tw-flex-col tw-min-h-full'>
            <PageHeader title={t(TRANSLATION_KEYS.USER.PAGE_NAMES.ONLINE.CONVERSATIONS)} />
            {isError &&
                <div className='tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12'>
                    <Icon name='alert-circle' size='xl' color='alert' />
                    <p className='tw-mt-3 tw-text-base tw-text-alert'>{t(T_KEYS.LOAD_ERROR.MESSAGE)}</p>
                    <p className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.LOAD_ERROR.RELOAD_PAGE_MESSAGE)}</p>
                </div>}
            {!isError && isLoading &&
                <div className='tw-relative tw-h-10'><Loader className='tw-max-h-12 tw-inset-0 tw-mt-2' /></div>}
            {!isError && !isLoading &&
                <div className='tw-flex tw-flex-col tw-flex-1'>
                    {/* Content row — explicit dvh height bypasses broken min-height flex chain in AuthLayoutControls */}
                    <div className='tw-flex tw-h-[calc(100dvh-12rem)] lg:tw-h-[calc(100dvh-15rem)]'>
                        {/* Left panel - conversation list */}
                        <div
                            className={cx(
                                'lg:tw-border-r tw-border-default-dimmed',
                                'lg:tw-w-80 lg:tw-min-w-[320px]',
                                mobilePanelView === 'list'
                                    ? 'tw-flex tw-flex-col tw-flex-1 lg:tw-flex-none'
                                    : 'tw-hidden lg:tw-flex lg:tw-flex-col'
                            )}
                        >
                            {/* Left header */}
                            <div
                                className={cx(
                                    'tw-px-4 tw-h-8 tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0'
                                )}
                            >
                                <span className='tw-flex-1 tw-text-lg tw-font-bold tw-text-dark'>
                                    {isArchivedTab
                                        ? t(T_KEYS.CONVERSATIONS.ARCHIVED_TAB)
                                        : t(T_KEYS.CONVERSATIONS.ACTIVE_TAB)}
                                    {' '}
                                    <span className='tw-text-default-dimmed tw-font-normal'>
                                        {'('}
                                        {totalCount}
                                        {')'}
                                    </span>
                                </span>
                                <Button
                                    className={isArchivedTab
                                        ? 'tw-bg-primary-lighten tw-border-primary-lighten hover:tw-border-primary-lighten'
                                        : undefined}
                                    color='default'
                                    rounded
                                    title={isArchivedTab
                                        ? t(T_KEYS.CONVERSATIONS.ACTIVE_TAB)
                                        : t(T_KEYS.CONVERSATIONS.ARCHIVED_TAB)}
                                    width='auto'
                                    onClick={() => handleTabChange(isArchivedTab ? 'active' : 'archived')}
                                >
                                    <Icon
                                        color={isArchivedTab ? 'primary' : 'default-dimmed'}
                                        name='archive'
                                        size='sm'
                                    />
                                </Button>
                            </div>
                            <div className='tw-px-3 tw-py-3'>
                                <Input
                                    inputClassName={cx(
                                        'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-rounded-lg',
                                        'tw-border tw-border-default tw-bg-layout',
                                        'tw-text-default tw-outline-none',
                                        'focus:tw-border-primary focus:tw-ring-1 focus:tw-ring-primary'
                                    )}
                                    name='search'
                                    placeholder={t(T_KEYS.CONVERSATIONS.SEARCH_PLACEHOLDER)}
                                    styled={false}
                                    transparent
                                    value={searchQuery}
                                    onChange={(val) => setSearchQuery(val)}
                                />
                            </div>
                            {/* Scrollable conversation list */}
                            <div className='tw-flex-1 tw-min-h-0 tw-overflow-y-auto'>
                                {isEmpty(filteredItems) &&
                                    <div className='tw-flex tw-flex-col tw-items-center tw-py-8 tw-px-4'>
                                        <Icon color='default-dimmed' name='text-chat-history' size='xl' />
                                        <p className='tw-mt-3 tw-text-sm tw-text-center tw-text-default-dimmed'>
                                            {t(T_KEYS.CONVERSATIONS.EMPTY_CONVERSATIONS)}
                                        </p>
                                    </div>}
                                {map(filteredItems, (item) => {
                                    const isSelected = selectedId === item.id;
                                    const hasNewActivity = !isArchivedTab
                                    && item.lastActivityAt
                                    && item.lastActivityAt !== lastSeenTimestamps[item.id]
                                    && !isSelected;
                                    const userInfo = item.client?.userInfo;
                                    const initials = sessionUtils.getInitials(userInfo?.firstName, userInfo?.lastName);
                                    const colorWord = userInfo?.email || userInfo?.phone || userInfo?.id || item.id;
                                    return (
                                        <div
                                            key={item.id}
                                            className={cx(
                                                'tw-flex tw-items-center tw-px-4 tw-py-3',
                                                'tw-cursor-pointer tw-transition-colors tw-outline-none',
                                                'tw-rounded-lg tw-mx-2 tw-mb-1 tw-border tw-border-transparent',
                                                isSelected ? 'tw-bg-primary tw-bg-opacity-10' : 'hover:tw-bg-primary-lighten'
                                            )}
                                            onClick={() => handleConversationClick(item)}
                                        >
                                            <div
                                                className='tw-relative tw-mr-3 tw-flex-shrink-0'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const clientUserInfo = item.client?.userInfo;
                                                    setParticipantModalProps({
                                                        open: true,
                                                        participant: toAnyParticipant(
                                                            clientUserInfo?.id || item.id,
                                                            ParticipantRole.Client,
                                                            {userInfo: clientUserInfo},
                                                        ),
                                                    });
                                                }}
                                            >
                                                <Initials initials={initials || '?'} colorWord={colorWord} size='base' />
                                                {hasNewActivity &&
                                                    <span className={cx(
                                                        'tw-absolute -tw-top-0.5 -tw-right-0.5',
                                                        'tw-w-2.5 tw-h-2.5 tw-rounded-full',
                                                        'tw-bg-primary tw-border-2 tw-border-light'
                                                    )}
                                                    />}
                                            </div>
                                            <div className='tw-flex-1 tw-min-w-0'>
                                                {/* Row 1: primary name + time */}
                                                <div className='tw-flex tw-items-baseline tw-justify-between tw-gap-2'>
                                                    <span
                                                        className={cx(
                                                            'tw-text-sm tw-truncate',
                                                            hasNewActivity ? 'tw-font-bold tw-text-dark' : 'tw-font-semibold tw-text-dark'
                                                        )}
                                                    >
                                                        {getClientInfo(item).primary}
                                                    </span>
                                                    {item.lastActivityAt &&
                                                        <span
                                                            className={cx(
                                                                'tw-text-xs tw-flex-shrink-0',
                                                                hasNewActivity
                                                                    ? 'tw-text-primary tw-font-semibold'
                                                                    : 'tw-text-default-dimmed'
                                                            )}
                                                        >
                                                            {formatDate(
                                                                item.lastActivityAt, timeZone,
                                                                dateUtils.getDateTimeFormat(dateFormat, timeFormat),
                                                            )}
                                                        </span>}
                                                </div>
                                                {/* Row 2: secondary name + status icon */}
                                                <div className='tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-0.5'>
                                                    <span className='tw-text-xs tw-text-default-dimmed tw-truncate'>
                                                        {getClientInfo(item).secondary || '\u00A0'}
                                                    </span>
                                                    {item.status === ConversationStatus.InSession &&
                                                        <Icon
                                                            className='tw-flex-shrink-0'
                                                            color='success'
                                                            name='agent-participant'
                                                            size='md'
                                                        />}
                                                    {item.status === ConversationStatus.Calling &&
                                                        <Icon
                                                            className='tw-flex-shrink-0'
                                                            color='alert'
                                                            name='agent-participant'
                                                            size='md'
                                                        />}
                                                    {item.status !== ConversationStatus.InSession
                                                    && item.status !== ConversationStatus.Calling &&
                                                        <Icon
                                                            className='tw-flex-shrink-0'
                                                            color='default-dimmed'
                                                            name='chatbot'
                                                            size='md'
                                                        />}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {isArchivedTab && archivedPage < archivedPages &&
                                    <div className='tw-flex tw-justify-center tw-py-3'>
                                        <Button
                                            color='default'
                                            loading={archivedLoading}
                                            rounded
                                            width='auto'
                                            onClick={handleLoadMore}
                                        >
                                            {t(TRANSLATION_KEYS.COMMON.BUTTONS.SHOW_MORE)}
                                        </Button>
                                    </div>}
                            </div>
                        </div>

                        {/* Right panel - chat content */}
                        <div
                            className={cx(
                                'tw-flex tw-flex-col',
                                mobilePanelView === 'chat' ? 'tw-flex-1' : 'tw-hidden lg:tw-flex lg:tw-flex-1'
                            )}
                        >
                            {/* Desktop conversation header */}
                            {selectedConversation &&
                                <div
                                    className={cx(
                                        'tw-hidden lg:tw-flex tw-items-center tw-gap-3',
                                        'tw-px-4 tw-py-2 tw-border-b tw-border-default-dimmed tw-flex-shrink-0',
                                        'tw-overflow-x-auto'
                                    )}
                                >
                                    <div
                                        className={cx(
                                            'tw-w-8 tw-h-8 tw-rounded-full tw-bg-primary',
                                            'tw-flex tw-items-center tw-justify-center tw-flex-shrink-0'
                                        )}
                                    >
                                        <Icon name='client-participant' color='light' size='sm' />
                                    </div>
                                    <div className='tw-flex-1 tw-min-w-0'>
                                        <div className='tw-text-sm tw-font-semibold tw-text-dark tw-truncate'>
                                            {getClientInfo(selectedConversation).primary}
                                        </div>
                                        {getClientInfo(selectedConversation).secondary &&
                                            <div className='tw-text-xs tw-text-default-dimmed tw-truncate'>
                                                {getClientInfo(selectedConversation).secondary}
                                            </div>}
                                    </div>
                                    {selectedConversation.channels?.length > 0 && (() => {
                                        const activeRef = selectedConversation.channels[selectedChannelIndex];
                                        const activeInfo = getChannelInfo(activeRef?.type);
                                        const isMulti = selectedConversation.channels.length > 1;
                                        const triggerChip = (
                                            <Chip
                                                className={cx(
                                                    '!tw-m-0 tw-flex tw-items-center tw-gap-1 tw-text-xs',
                                                    'tw-cursor-pointer tw-flex-shrink-0',
                                                )}
                                            >
                                                <Icon name={activeInfo.icon} size='xs' color='primary' />
                                                <span>{activeInfo.label}</span>
                                                {isMulti && <Icon name='chevron-down' size='xs' />}
                                            </Chip>
                                        );
                                        if (!isMulti) {
                                            return triggerChip;
                                        }
                                        return (
                                            <Dropdown position='bottom-start' triggerNode={triggerChip}>
                                                {selectedConversation.channels.map((ref, i) => {
                                                    const info = getChannelInfo(ref.type);
                                                    const isActive = selectedChannelIndex === i;
                                                    const unread = hasUnreadForChannel(ref.channelId);
                                                    return (
                                                        <DropdownItem
                                                            key={ref.channelId}
                                                            selected={isActive}
                                                            onClick={() => handleChannelClick(i)}
                                                        >
                                                            <span className='tw-flex tw-items-center tw-gap-2'>
                                                                <Icon
                                                                    name={info.icon}
                                                                    size='sm'
                                                                    color={isActive ? 'primary' : 'default'}
                                                                />
                                                                <span className={isActive ? 'tw-font-semibold' : ''}>
                                                                    {info.label}
                                                                </span>
                                                                {unread &&
                                                                    <span
                                                                        className={cx(
                                                                            'tw-w-2 tw-h-2 tw-rounded-full',
                                                                            'tw-bg-success tw-flex-shrink-0'
                                                                        )}
                                                                    />}
                                                            </span>
                                                        </DropdownItem>
                                                    );
                                                })}
                                            </Dropdown>
                                        );
                                    })()}
                                    <div className='tw-flex tw-items-center tw-gap-2 tw-ml-4 tw-flex-shrink-0'>
                                        {!isArchivedTab &&
                                            <Fragment>
                                                {(isAgentJoinedChannel || joined) &&
                                                    <Button
                                                        color='default'
                                                        icon='chatbot'
                                                        fontStyle='normal'
                                                        loading={leaving}
                                                        rounded
                                                        width='auto'
                                                        onClick={handleTransferToAiClick}
                                                    >
                                                        {t(T_KEYS.CONVERSATIONS.TRANSFER_TO_AI)}
                                                    </Button>}
                                                {!(isAgentJoinedChannel || joined) &&
                                                    <Button
                                                        color='default'
                                                        icon='agent-participant'
                                                        iconColor='primary'
                                                        fontStyle='normal'
                                                        loading={joining}
                                                        rounded
                                                        width='auto'
                                                        onClick={handleJoin}
                                                    >
                                                        {t(T_KEYS.CONVERSATIONS.TAKE_OVER)}
                                                    </Button>}
                                                <ConfirmButton onConfirm={handleEndConversation}>
                                                    {({onClick}) => (
                                                        <Button
                                                            color='default'
                                                            icon='archive'
                                                            iconColor='alert'
                                                            fontStyle='normal'
                                                            loading={endingConversation}
                                                            rounded
                                                            width='auto'
                                                            onClick={onClick}
                                                        >
                                                            {t(T_KEYS.CONVERSATIONS.ARCHIVE)}
                                                        </Button>
                                                    )}
                                                </ConfirmButton>
                                            </Fragment>}
                                        {isArchivedTab &&
                                            <Button
                                                color='default'
                                                icon='corner-left-up'
                                                iconColor='primary'
                                                fontStyle='normal'
                                                loading={rejoining}
                                                rounded
                                                width='auto'
                                                onClick={handleRejoin}
                                            >
                                                {t(T_KEYS.CONVERSATIONS.RESUME)}
                                            </Button>}
                                    </div>
                                </div>}
                            {/* Mobile header */}
                            <div
                                className={cx(
                                    'lg:tw-hidden tw-flex tw-items-center tw-gap-2',
                                    'tw-px-4 tw-py-2 tw-border-b tw-border-default-dimmed tw-overflow-x-auto'
                                )}
                            >
                                <Button
                                    color='default'
                                    rounded
                                    width='auto'
                                    onClick={handleMobileBack}
                                >
                                    <Icon name='chevron-left' size='sm' />
                                </Button>
                                {selectedConversation &&
                                    <div className='tw-flex-1 tw-min-w-0'>
                                        <div className='tw-text-base tw-font-semibold tw-text-dark tw-truncate'>
                                            {getClientInfo(selectedConversation).primary}
                                        </div>
                                        {getClientInfo(selectedConversation).secondary &&
                                            <div className='tw-text-xs tw-text-default-dimmed tw-truncate'>
                                                {getClientInfo(selectedConversation).secondary}
                                            </div>}
                                    </div>}
                                {selectedConversation?.channels?.length === 1 &&
                                    (() => {
                                        const info = getChannelInfo(selectedConversation.channels[0].type);
                                        return (
                                            <span className='tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary tw-flex-shrink-0'>
                                                <Icon name={info.icon} size='xs' color='primary' />
                                                {info.label}
                                            </span>
                                        );
                                    })()}
                                {selectedConversation?.channels?.length > 1 &&
                                    <Dropdown
                                        position='bottom-end'
                                        triggerNode={
                                            <Button color='default' rounded width='auto'>
                                                <span className='tw-flex tw-items-center tw-gap-1 tw-text-xs'>
                                                    <Icon
                                                        name={getChannelInfo(selectedChannelRef?.type).icon}
                                                        size='xs'
                                                        color='primary'
                                                    />
                                                    <span className='tw-text-primary'>
                                                        {getChannelInfo(selectedChannelRef?.type).label}
                                                    </span>
                                                    {hasAnyUnread &&
                                                        <span className='tw-w-2 tw-h-2 tw-rounded-full tw-bg-success tw-flex-shrink-0' />}
                                                    <Icon name='chevron-down' size='xs' />
                                                </span>
                                            </Button>
                                        }
                                    >
                                        {selectedConversation.channels.map((ref, i) => {
                                            const channelInfo = getChannelInfo(ref.type);
                                            const isActive = selectedChannelIndex === i;
                                            const unread = hasUnreadForChannel(ref.channelId);
                                            const cached = loadedChannels.find((ch) => ch.id === ref.channelId);
                                            const contactInfo = cached ? getChannelContactInfo(cached) : '';
                                            return (
                                                <DropdownItem
                                                    key={ref.channelId}
                                                    selected={isActive}
                                                    onClick={() => handleChannelClick(i)}
                                                >
                                                    <span className='tw-flex tw-items-center tw-gap-2'>
                                                        <Icon
                                                            name={channelInfo.icon}
                                                            size='sm'
                                                            color={isActive ? 'primary' : 'default'}
                                                        />
                                                        <span className={isActive ? 'tw-font-semibold' : ''}>
                                                            {channelInfo.label}
                                                        </span>
                                                        {contactInfo &&
                                                            <span className='tw-text-default-dimmed'>
                                                                {contactInfo}
                                                            </span>}
                                                        {unread &&
                                                            <span
                                                                className={cx(
                                                                    'tw-w-2 tw-h-2 tw-rounded-full',
                                                                    'tw-bg-success tw-flex-shrink-0'
                                                                )}
                                                            />}
                                                    </span>
                                                </DropdownItem>
                                            );
                                        })}
                                    </Dropdown>}
                                {!isArchivedTab && selectedConversation &&
                                    <div className='tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0'>
                                        {(isAgentJoinedChannel || joined) &&
                                            <Icon
                                                name='chatbot'
                                                size='md'
                                                color='default'
                                                button
                                                onClick={handleTransferToAiClick}
                                            />}
                                        {!(isAgentJoinedChannel || joined) &&
                                            <Icon
                                                name='agent-participant'
                                                size='md'
                                                color='primary'
                                                button
                                                onClick={handleJoin}
                                            />}
                                        <ConfirmButton
                                            iconProps={{name: 'archive', size: 'md', color: 'alert', button: true}}
                                            onConfirm={handleEndConversation}
                                        />
                                    </div>}
                                {isArchivedTab && selectedConversation &&
                                    <Icon
                                        name='corner-left-up'
                                        size='md'
                                        color='primary'
                                        button
                                        onClick={handleRejoin}
                                    />}
                            </div>
                            {!selectedConversation &&
                                <div
                                    className={cx(
                                        'tw-flex-1 tw-flex tw-flex-col',
                                        'tw-items-center tw-justify-center',
                                        'tw-text-default-dimmed'
                                    )}
                                >
                                    <Icon name='text-chat-history' size='xl' color='default-dimmed' />
                                    <p className='tw-mt-4 tw-text-lg'>
                                        {t(T_KEYS.CONVERSATIONS.EMPTY_CONVERSATIONS)}
                                    </p>
                                </div>}
                            {selectedConversation &&
                                <Fragment>
                                    <div
                                        className={cx(
                                            'tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overscroll-contain',
                                            'vs-session-text-chat tw-p-4',
                                        )}
                                    >
                                        {chatLoading &&
                                            <div className='tw-flex tw-justify-center tw-py-4'>
                                                <Loader className='tw-max-h-8' />
                                            </div>}
                                        {!chatLoading &&
                                            <TextChatMessageList
                                                messages={chatMessages}
                                                participants={chatParticipants}
                                                localParticipantId={localParticipantId}
                                                userParticipantIds={userParticipantIds}
                                                downloadFile={() => null}
                                                messagesEndRef={messagesEndRef}
                                                downloadingFiles={[]}
                                                typingRemoteParticipantIds={[]}
                                                downloadFileDisabled
                                                conversationMode
                                            />}
                                    </div>
                                    {/* Message input - shown when agent is joined */}
                                    {!isArchivedTab && (isAgentJoinedChannel || joined) &&
                                        <div
                                            className='tw-border-t tw-border-default-dimmed tw-px-4 tw-py-3 tw-flex tw-gap-2'
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSendMessage();
                                                }
                                            }}
                                        >
                                            <Input
                                                inputClassName={cx(
                                                    'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-rounded-lg',
                                                    'tw-border tw-border-default tw-bg-layout',
                                                    'tw-text-default tw-outline-none',
                                                    'focus:tw-border-primary focus:tw-ring-1 focus:tw-ring-primary'
                                                )}
                                                className='tw-flex-1'
                                                name='message'
                                                placeholder={t(T_KEYS.CONVERSATIONS.MESSAGE_PLACEHOLDER)}
                                                styled={false}
                                                transparent
                                                value={messageText}
                                                onChange={(val) => setMessageText(val)}
                                            />
                                            <Button
                                                color='primary'
                                                disabled={!messageText.trim()}
                                                loading={sending}
                                                rounded
                                                width='normal'
                                                onClick={handleSendMessage}
                                            >
                                                {t(T_KEYS.CONVERSATIONS.SEND)}
                                            </Button>
                                        </div>}
                                </Fragment>}
                        </div>
                    </div>
                </div>}
            <ParticipantDetailsModal
                open={participantModalProps.open}
                participant={participantModalProps.participant}
                onClose={() => setParticipantModalProps({open: false})}
            />
            <ConfirmModal
                open={transferModalOpen}
                title={t(T_KEYS.CONVERSATIONS.TRANSFER_TO_AI_MODAL_TITLE)}
                okText={t(T_KEYS.CONVERSATIONS.TRANSFER_TO_AI_MODAL_CONFIRM)}
                onClose={(confirm) => {
                    setTransferModalOpen(false);
                    if (confirm) {
                        handleLeave(transferMessage || undefined);
                    }
                }}
            >
                <TextArea
                    rows={3}
                    maxLength={300}
                    placeholder={t(T_KEYS.CONVERSATIONS.TRANSFER_TO_AI_MODAL_PLACEHOLDER)}
                    styled={false}
                    textareaClassName={cx(
                        'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-rounded-lg tw-resize-none',
                        'tw-border tw-border-default tw-bg-layout tw-text-default tw-outline-none',
                        'focus:tw-border-primary focus:tw-ring-1 focus:tw-ring-primary'
                    )}
                    value={transferMessage}
                    onChange={(val) => setTransferMessage(val)}
                />
            </ConfirmModal>
        </div>
    );
};
